// اجزای مشترک SCR-STAFF-905 (AP-PLT-03): کلیدهای Query، وضعیت‌ها، دانلود فایل با توکن مستأجر و اتصال زنده Hub PLT
// (`importJobChanged` فقط شناسه، وضعیت و شمارش — بدون داده ردیف). اتصال قابل تزریق است تا تست‌ها رویداد ساختگی بفرستند.
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import type { ImportDependent, ImportJobStatus, ImportJobView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, StatusChip, type StatusTone } from '@phoenix/ui';
import { createContext, useContext, useEffect, useRef } from 'react';

export const SCR_STAFF_905 = 'SCR-STAFF-905';
export const IMPORT_PATH = '/settings/import';

export const importKeys = {
  all: (tenantId: string | undefined) => ['platform', 'imports', tenantId] as const,
  types: (tenantId: string | undefined) => ['platform', 'imports', tenantId, 'types'] as const,
  list: (tenantId: string | undefined) => ['platform', 'imports', tenantId, 'list'] as const,
  job: (tenantId: string | undefined, id: string) => ['platform', 'imports', tenantId, 'job', id] as const,
  mapping: (tenantId: string | undefined, id: string) => ['platform', 'imports', tenantId, 'mapping', id] as const,
  errors: (tenantId: string | undefined, id: string) => ['platform', 'imports', tenantId, 'errors', id] as const,
  rollback: (tenantId: string | undefined, id: string) => ['platform', 'imports', tenantId, 'rollback', id] as const,
};

/** وضعیت‌های در جریان پس‌زمینه (خواندن دوره‌ای). */
export const BUSY_STATUSES: readonly ImportJobStatus[] = ['Validating', 'Running'];

/** وضعیت‌های پس از اجرا. */
export const EXECUTED_STATUSES: readonly ImportJobStatus[] = ['Completed', 'CompletedWithErrors', 'Failed', 'RolledBack'];

export function isBusy(job: ImportJobView | undefined): boolean {
  return job !== undefined && BUSY_STATUSES.includes(job.status);
}

export function notValidCount(job: ImportJobView): number {
  return Number(job.counts.invalid) + Number(job.counts.duplicateInFile) + Number(job.counts.duplicateExisting);
}

const TONES: Record<ImportJobStatus, StatusTone> = {
  Uploaded: 'confirmed',
  Mapped: 'confirmed',
  Validating: 'pending',
  Validated: 'pending',
  ValidationFailed: 'error',
  Running: 'pending',
  Completed: 'active',
  CompletedWithErrors: 'active',
  Failed: 'error',
  RolledBack: 'inactive',
};

export function ImportStatusChip({ status }: { status: ImportJobStatus }) {
  return (
    <span data-testid="import-status" data-status={status}>
      <StatusChip tone={TONES[status]} labelKey={`plt:import.status.${status}`} />
    </span>
  );
}

/** متن یک گروه وابسته بازگشت (AC-PLT-063.2): نوع، شمار و نمونه شماره پرونده یا کد خدمت. */
export function DependentLine({ dependent }: { dependent: ImportDependent }) {
  const { t } = useTranslation('plt');
  const examples = dependent.examples.filter(Boolean);
  return (
    <li data-testid="rollback-dependent" data-kind={dependent.kindKey}>
      {t('plt:import.dependents.line', { kind: t(dependent.kindKey), count: faDigits(Number(dependent.count)) })}
      {examples.length > 0 && (
        <>
          {' — '}
          {t('plt:import.dependents.examples', { examples: '' })}
          <bdi dir="ltr">{examples.join(t('plt:import.list_separator'))}</bdi>
        </>
      )}
    </li>
  );
}

/** ذخیره Blob پاسخ (فایل خطا یا قالب) با نام فایل؛ توکن در سرآیند از کلاینت مستأجر. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// --- اتصال زنده Hub PLT (AC-PLT-062.1 «پیشرفت Realtime»؛ C-AGENT-357) ---

export interface ImportJobChanged {
  jobId: string;
  status: ImportJobStatus;
  totalRows: number;
  processedRows: number;
  importedRows: number;
  skippedRows: number;
}

export interface ImportRealtimeConnection {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  onChanged: (handler: (message: ImportJobChanged) => void) => void;
}

export type ImportRealtimeFactory = (options: { url: string; getToken: () => string | undefined }) => ImportRealtimeConnection;

export const IMPORT_HUB_PATH = '/hubs/platform';

/** اتصال واقعی SignalR (LongPolling تا توکن فقط در سرآیند Authorization بماند). */
export const signalRImportFactory: ImportRealtimeFactory = ({ url, getToken }) => {
  const connection = new HubConnectionBuilder()
    .withUrl(url, { transport: HttpTransportType.LongPolling, accessTokenFactory: () => getToken() ?? '' })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();
  return {
    start: () => connection.start(),
    stop: () => connection.stop(),
    onChanged: (handler) => connection.on('importJobChanged', handler),
  };
};

export interface ImportRealtimeOptions {
  apiBaseUrl: string;
  factory: ImportRealtimeFactory | false;
}

export const ImportRealtimeContext = createContext<ImportRealtimeOptions>({ apiBaseUrl: '', factory: false });

/** گوش دادن به پیشرفت کارهای ورود فقط وقتی کاری در جریان است (UI همزمان خواندن دوره‌ای دارد). */
export function useImportChanged(enabled: boolean, listener: (message: ImportJobChanged) => void): void {
  const { apiBaseUrl, factory } = useContext(ImportRealtimeContext);
  const { session } = useTenantSession();
  const access = useAccess();
  const token = useRef<string | undefined>(undefined);
  token.current = session?.accessToken;
  const latest = useRef(listener);
  latest.current = listener;
  const create = factory === false ? null : factory;
  const active = enabled && create !== null && access.can('plt.import.run');

  useEffect(() => {
    if (!active || !create) {
      return undefined;
    }
    const base = apiBaseUrl || (typeof window === 'undefined' ? '' : window.location.origin);
    const connection = create({ url: `${base}${IMPORT_HUB_PATH}`, getToken: () => token.current });
    connection.onChanged((message) => latest.current(message));
    let started = true;
    connection.start().catch(() => {
      started = false;
    });
    return () => {
      if (started) {
        void connection.stop().catch(() => undefined);
      }
    };
  }, [active, apiBaseUrl, create]);
}
